import React, { Component, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { logger }   from "@/system/logger";
import { eventBus } from "@/runtime/eventBus";
import { isChunkLoadError } from "@/system/chunkError";

interface Props {
  children: ReactNode;
  name?: string;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorGuard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const name = this.props.name || "unknown";
    logger.error(`ErrorGuard [${name}]`, {
      message:        error.message,
      stack:          error.stack,
      componentStack: info.componentStack,
    });
    eventBus.emit({ type: "crash:detected", containerId: name });
  }

  handleRetry = (): void => {
    const name = this.props.name || "unknown";
    eventBus.emit({ type: "crash:recovered", containerId: name });
    // A stale-chunk error can't be cleared by re-rendering the same failed
    // dynamic import - the fix is a full reload to fetch the fresh bundle.
    // A plain re-mount (setState) is correct for all other errors.
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-3">
          <div className="h-12 w-12 rounded-xl bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <p className="text-sm font-medium">Something went wrong</p>
            <p className="text-xs text-muted-foreground mt-1">
              {this.props.name ? `The ${this.props.name} encountered an error.` : "An unexpected error occurred."}
            </p>
            {this.state.error && (
              <p className="text-[10px] text-muted-foreground mt-2 font-mono max-w-xs truncate">
                {this.state.error.message}
              </p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={this.handleRetry} className="gap-1.5">
            <RefreshCw className="h-3 w-3" /> Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
